/* eslint-disable max-lines */
import { useEffect, useState, useCallback, useRef } from 'react'
import { getCachedAuthSession } from 'services/authSessionCache'
import Environment from 'helpers/environment'
import { v4 as uuidv4 } from 'uuid'

import { CitationFile, Citation } from 'types/Chat'

export interface ChatMessage {
  id: string
  sender: 'You' | 'Tech Assistant'
  content: string
  complete?: boolean
  searching?: boolean
  searchingText?: string
  context?: Citation[]
  streamingCitations?: Citation[]
  citationsComplete?: boolean
  responseBlockId?: string
  nbrRemainingMessages?: number
}

interface WebSocketEvent {
  event: string
  completion?: string
  responseBlockId?: string
  citation?: Citation
  citations?: Citation[]
  nbrRemainingMessages?: number
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error'

interface UseTechAssistantChatReturn {
  messages: ChatMessage[]
  sendMessage: (message: string, conversationId?: string) => Promise<void>
  citationFiles: CitationFile[]
  freeMessageCount: number | undefined
  connectionStatus: ConnectionStatus
  sessionId: string
  resetSession: () => void
  connect: () => Promise<void>
  disconnect: () => void
}

const useTechAssistantChat = (remainingMessages: number | undefined): UseTechAssistantChatReturn => {
  const initialMessage = 'Hello! How can I help you?'

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: uuidv4(),
      sender: 'Tech Assistant',
      content: initialMessage
    }
  ])

  const [citationFiles, setCitationFiles] = useState<CitationFile[]>([])
  const [freeMessageCount, setFreeMessageCount] = useState<number | undefined>(remainingMessages)
  const [sessionId, setSessionId] = useState<string>(uuidv4())
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected')

  const wsClientRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleMessage = useCallback((message: MessageEvent) => {
    const parsed: WebSocketEvent = JSON.parse(message.data)

    if (parsed.responseBlockId) {
      console.log(`Received event ${parsed.event} with responseBlockId:`, parsed.responseBlockId)
    }

    if (parsed.completion === 'messageLimitReached') {
      setFreeMessageCount(0)
      setMessages((prevMessages) =>
        prevMessages.map((msg, index) =>
          index === prevMessages.length - 1
            ? {
              ...msg,
              complete: true,
              searching: false
            }
            : msg
        )
      )
      return
    }

    switch (parsed.event) {
      case 'citations-loading':
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.responseBlockId === parsed.responseBlockId
              ? {
                ...msg,
                searching: true,
                searchingText: 'Finding supporting documentation...',
                streamingCitations: [],
                citationsComplete: false
              }
              : msg
          )
        )
        break

      case 'citation-result':
        setCitationFiles((prevFiles) => {
          const ref = parsed.citation?.retrievedReferences?.[0]
          const s3Uri = ref?.location?.s3Location?.uri
          const fileLocation = ref?.metadata?.FileLocation
          const uniqueKey = s3Uri || fileLocation
          if (!uniqueKey) return prevFiles

          const newFile: CitationFile = {
            cacheTitle: ref?.metadata?.CacheTitle || '',
            fileLocation,
            datePublished: ref?.metadata?.DatePublished,
            litType: ref?.metadata?.LitTypeDescription,
            model: ref?.metadata?.Model,
            content: typeof ref?.content === 'object' && ref?.content !== null && 'text' in (ref.content as object)
              ? (ref.content as { text: string }).text
              : undefined,
            s3Uri
          }
          const isDuplicate = prevFiles.some((f) => (f.s3Uri || f.fileLocation) === uniqueKey)
          return isDuplicate ? prevFiles : [...prevFiles, newFile]
        })

        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.responseBlockId === parsed.responseBlockId
              ? {
                ...msg,
                streamingCitations: [...(msg.streamingCitations || []), parsed.citation!]
              }
              : msg
          )
        )
        break

      case 'citations-done':
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.responseBlockId === parsed.responseBlockId
              ? {
                ...msg,
                context: msg.streamingCitations || [],
                searching: false,
                searchingText: undefined,
                citationsComplete: true,
                streamingCitations: undefined
              }
              : msg
          )
        )
        break

      case 'remaining-message-count':
        setFreeMessageCount(parsed.nbrRemainingMessages)
        break

      case 'end-response':
        setMessages((prevMessages) =>
          prevMessages.map((msg, index) =>
            index === prevMessages.length - 1
              ? {
                ...msg,
                complete: true,
                responseBlockId: parsed.responseBlockId
              }
              : msg
          )
        )
        break

      case 'partial-response':
        setMessages((prevMessages) =>
          prevMessages.map((msg, index) => {
            if (msg.responseBlockId === parsed.responseBlockId) {
              return {
                ...msg,
                content: msg.content + (parsed.completion || ''),
                searching: false
              }
            }
            if (
              index === prevMessages.length - 1 &&
              !msg.responseBlockId &&
              msg.sender === 'Tech Assistant'
            ) {
              return {
                ...msg,
                content: msg.content + (parsed.completion || ''),
                searching: false,
                responseBlockId: parsed.responseBlockId
              }
            }
            return msg
          })
        )
        break
    }
  }, [])

  const connect = useCallback(async () => {
    if (wsClientRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    setConnectionStatus('connecting')

    try {
      const session = await getCachedAuthSession()
      const idToken = session.tokens?.idToken?.toString()

      if (!idToken) {
        setConnectionStatus('error')
        return
      }

      const host = Environment.chatbotWebSocketURL()
      const url = `${host}/?idToken=${encodeURIComponent(idToken)}`

      const client = new WebSocket(url)

      client.onopen = () => {
        setConnectionStatus('connected')
      }

      client.onmessage = handleMessage

      client.onerror = () => {
        setConnectionStatus('error')
      }

      client.onclose = () => {
        setConnectionStatus('disconnected')

        // Auto-reconnect after 2 seconds
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current)
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          connect()
        }, 2000)
      }

      wsClientRef.current = client
    } catch {
      setConnectionStatus('error')
    }
  }, [handleMessage])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (wsClientRef.current) {
      wsClientRef.current.close()
      wsClientRef.current = null
    }
    setConnectionStatus('disconnected')
  }, [])

  const sendMessage = useCallback(async (userMessage: string, conversationId?: string) => {
    if (!userMessage.trim()) {
      return
    }

    // Add user message and placeholder for assistant response
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        id: uuidv4(),
        sender: 'You',
        content: userMessage
      },
      {
        id: uuidv4(),
        sender: 'Tech Assistant',
        content: '',
        complete: false,
        searching: true
      }
    ])

    try {
      const session = await getCachedAuthSession()
      const idToken = session.tokens?.idToken?.toString()

      if (!idToken) {
        throw new Error('No auth token available')
      }

      if (!wsClientRef.current || wsClientRef.current.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket not connected')
      }

      wsClientRef.current.send(
        JSON.stringify({
          source: 'web',
          sessionId: conversationId || sessionId,
          message: userMessage,
          idToken,
          type: 'authentication'
        })
      )
    } catch (error) {
      setMessages((prevMessages) => [
        ...prevMessages.slice(0, -1),
        {
          id: uuidv4(),
          sender: 'Tech Assistant',
          content: `[error: ${error instanceof Error ? error.message : 'Unknown error'}]`,
          complete: true
        }
      ])
    }
  }, [sessionId])

  const resetSession = useCallback(() => {
    setSessionId(uuidv4())
    setMessages([
      {
        id: uuidv4(),
        sender: 'Tech Assistant',
        content: initialMessage
      }
    ])
  }, [initialMessage])

  useEffect(() => {
    connect()

    return () => {
      disconnect()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    messages,
    citationFiles,
    sendMessage,
    freeMessageCount,
    connectionStatus,
    sessionId,
    resetSession,
    connect,
    disconnect
  }
}

export default useTechAssistantChat
